import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { loadCustomerTicketHistory } from '@/lib/customer-ticket-history'
import {
  Alert,
  Box,
  Button,
  Flex,
  Group,
  Modal,
  Portal,
  Select,
  Stack,
  Text,
  Textarea,
  useMantineTheme,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { showNotification } from '@mantine/notifications'
import { TicketKnowledgePanel } from '@/components/knowledge/TicketKnowledgePanel'
import { OnBreakWorkBanner } from '@/components/tickets/OnBreakWorkBanner'
import { ParallelCustomerTicketsBanner } from '@/components/tickets/ParallelCustomerTicketsBanner'
import { useParallelCustomerTickets } from '@/components/tickets/useParallelCustomerTickets'
import { TicketActionModals } from '@/components/tickets/TicketActionModals'
import { TicketAiInstructionModal } from '@/components/tickets/TicketAiInstructionModal'
import { TicketCustomerAttachmentsModal } from '@/components/tickets/TicketCustomerAttachmentsModal'
import { TicketCustomerIdentityModals } from '@/components/tickets/TicketCustomerIdentityModals'
import { TicketMessagesPanel } from '@/components/tickets/TicketMessagesPanel'
import { TicketPreviousTicketsModal } from '@/components/tickets/TicketPreviousTicketsModal'
import { TicketReplyComposer } from '@/components/tickets/TicketReplyComposer'
import { TicketReplySlashMenu } from '@/components/tickets/TicketReplySlashMenu'
import { TicketReplyToolsModals } from '@/components/tickets/TicketReplyToolsModals'
import { TicketSidebarPanel } from '@/components/tickets/TicketSidebarPanel'
import { TicketTimelineModal } from '@/components/tickets/TicketTimelineModal'
import { SimilarClosedTicketsPanel } from '@/components/tickets/SimilarClosedTicketsPanel'
import { useTicketAssigneeSelect } from '@/components/tickets/useTicketAssigneeSelect'
import { useTicketAiInstruction } from '@/components/tickets/useTicketAiInstruction'
import { useTicketCustomerIdentifiers } from '@/components/tickets/useTicketCustomerIdentifiers'
import { useTicketPreviousTickets } from '@/components/tickets/useTicketPreviousTickets'
import { useTicketReplySlash } from '@/components/tickets/useTicketReplySlash'
import { useTicketReplyAttachments } from '@/components/tickets/useTicketReplyAttachments'
import { useTicketReplyTools } from '@/components/tickets/useTicketReplyTools'
import { useTicketDetailViewModel } from '@/components/tickets/useTicketDetailViewModel'
import { useTicketHeaderControls } from '@/components/tickets/useTicketHeaderControls'
import { useTicketManagementActions } from '@/components/tickets/useTicketManagementActions'
import { useTicketSdAdminActions } from '@/components/tickets/useTicketSdAdminActions'
import { useTicketTimeline } from '@/components/tickets/useTicketTimeline'
import { RefundCalculatorFloating } from '@/components/tickets/RefundCalculatorFloating'
import {
  mergeWsMessageIntoThread,
} from '@/components/tickets/TicketMessageThread'
import { getWebSocketUrl } from '@/lib/ws-url'
import { PageCenterLoader, PageShell } from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import { canAccessKnowledgeBase, defaultPermissionsForRole } from '@/lib/permissions'
import { notifyTicketsWorkspaceStale } from '@/lib/projects-events'
import {
  fetchNextUnassignedOpenTicket,
  fetchNextUnassignedTicketWide,
} from '@/lib/next-unassigned-ticket'
import { TICKETS_LIST_RETURN_URL_KEY } from '@/lib/open-ticket-navigation'
import { useWorkspaceTabs } from '@/lib/workspace-tabs-context'
import { mergeTicketMessages, ticketDetailMessageFromApi } from '@/lib/ticket-message-merge'
import {
  ticketPathSegmentFromListItem,
  ticketWorkspaceTabTitle,
} from '@/lib/ticket-display'
import { useTheme } from '@/lib/theme-context'
import { useOnShiftUsers } from '@/lib/on-shift-users-context'
import {
  getCachedAssigneeVisibleRoles,
  refreshAssigneeVisibleRolesFromApi,
  WORKSPACE_ASSIGNEE_ROLES_CHANGED,
} from '@/lib/workspace-assignee-roles'
import type {
  CustomerRow,
  MacroRow,
  ProjectCustomerFieldRow,
  ProjectDetailResponse,
  ProjectRow,
  ProjectTagRow,
  CustomerAttachmentItem,
  ProjectTemplateRow,
  ProjectUserIdentifierRow,
  TicketDetailMessage,
  TicketDetailTicket,
  TicketListItem,
  TicketProductReviewStatusResponse,
  TicketReviewStatusResponse,
  TicketViewerRow,
  WorkspaceUserRow,
} from '@/types/api'

const MESSAGES_LIMIT = 50
/** Сколько прошлых обращений показывать в боковой колонке до кнопки «Показать все». */
const PREVIOUS_TICKETS_SIDEBAR_PREVIEW = 8
const VIEWER_POLL_MS = 25_000

import {
  OPEN_HEARTBEAT_MS,
  REPLY_PANEL_LS_COLLAPSED,
  SD_ADMIN_API_ACTIONS,
  THREAD_POLL_MS,
  parseTicketRef,
  readReplyPanelCollapsed,
  readTicketReplyDraft,
  removeTicketReplyDraft,
  writeTicketReplyDraft,
} from '@/components/tickets/ticket-detail-page-helpers'


export function TicketDetailPage() {
  const { ticketRef } = useParams<{ ticketRef: string }>()
  const ticketId = useMemo(() => parseTicketRef(ticketRef), [ticketRef])
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isOnShift: isUserOnShift } = useOnShiftUsers()
  const [assigneeVisibleRoles, setAssigneeVisibleRoles] = useState<string[] | null>(
    () => getCachedAssigneeVisibleRoles(),
  )
  useEffect(() => {
    void refreshAssigneeVisibleRolesFromApi()
      .then(setAssigneeVisibleRoles)
      .catch(() => {})
    const onChange = () => setAssigneeVisibleRoles(getCachedAssigneeVisibleRoles())
    window.addEventListener(WORKSPACE_ASSIGNEE_ROLES_CHANGED, onChange)
    return () => window.removeEventListener(WORKSPACE_ASSIGNEE_ROLES_CHANGED, onChange)
  }, [])
  const { openOrSelectTab, updateActiveTabTitle, closeTab, activeTabId, tabs } = useWorkspaceTabs()
  const { theme: appTheme } = useTheme()
  const classicBadgeDark = appTheme === 'dark'
  const theme = useMantineTheme()
  const isSmUp = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`) ?? false
  /** Двухколоночный макет (переписка + сайдбар) с 1100px — как при lg 1280; иначе 1100–1279 переписка теряется в колонке. */
  const isTicketWideLayout = useMediaQuery('(min-width: 1100px)') ?? false
  /** Телефон: отдельные лимиты высоты переписки / ответа / сайдбара */
  const isPhoneLayout = !isSmUp

  const [ticket, setTicket] = useState<TicketDetailTicket | null>(null)
  const [messages, setMessages] = useState<TicketDetailMessage[]>([])
  const [messagesTotal, setMessagesTotal] = useState(0)
  const [messagesOffset, setMessagesOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [users, setUsers] = useState<WorkspaceUserRow[]>([])
  const [tags, setTags] = useState<ProjectTagRow[]>([])
  const [templates, setTemplates] = useState<ProjectTemplateRow[]>([])
  const [requireResolutionComment, setRequireResolutionComment] = useState(false)
  const [refundCalculatorEnabled, setRefundCalculatorEnabled] = useState(false)
  const [refundCalculatorOpen, setRefundCalculatorOpen] = useState(false)
  const [customer, setCustomer] = useState<CustomerRow | null>(null)
  const [viewers, setViewers] = useState<TicketViewerRow[]>([])
  const [previousTickets, setPreviousTickets] = useState<TicketListItem[]>([])
  const [previousTicketsModalOpen, setPreviousTicketsModalOpen] = useState(false)
  const [kbPanelOpen, setKbPanelOpen] = useState(false)
  const [similarPanelOpen, setSimilarPanelOpen] = useState(false)
  const [customerAttachments, setCustomerAttachments] = useState<CustomerAttachmentItem[]>([])
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false)
  const [attachmentsLoading, setAttachmentsLoading] = useState(false)
  const [attachmentsLoaded, setAttachmentsLoaded] = useState(false)
  const [workspaceProjects, setWorkspaceProjects] = useState<ProjectRow[]>([])
  const canViewKnowledgeBase = canAccessKnowledgeBase(
    defaultPermissionsForRole(user?.role || undefined),
    user?.role,
  )

  const [replyText, setReplyText] = useState('')
  /** Если ответ начат с шаблона — для учёта использования на бэкенде. */
  const [replyTemplateId, setReplyTemplateId] = useState<string | null>(null)
  /** Сообщение, на которое отвечаем цитатой (как currentQuotedMessage в старом фронте). */
  const [quotedMessage, setQuotedMessage] = useState<TicketDetailMessage | null>(null)
  const [replyTarget, setReplyTarget] = useState<'customer' | 'internal'>('customer')
  const {
    addPendingInstaller,
    appendFiles: appendReplyFiles,
    files,
    imageBlobUrls: replyImageBlobUrls,
    pendingInstallerIds,
    pendingInstallers,
    removeFileAt: removeReplyFileAt,
    removePendingInstallerAt,
    resetAttachments: resetReplyAttachments,
    replyFileIconResetRef,
  } = useTicketReplyAttachments()

  const [sending, setSending] = useState(false)
  /** Обновление ленты сообщений без полноэкранного лоадера карточки. */
  const [threadRefreshing, setThreadRefreshing] = useState(false)
  const sendingRef = useRef(false)
  const threadRefreshSeq = useRef(0)
  const messagesOffsetRef = useRef(0)
  useEffect(() => {
    sendingRef.current = sending
  }, [sending])
  const threadRefreshingRef = useRef(false)
  const threadWsConnectedRef = useRef(false)
  const deliveryFailureNotifiedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    threadRefreshingRef.current = threadRefreshing
  }, [threadRefreshing])

  const [replyPanelCollapsed, setReplyPanelCollapsed] = useState(readReplyPanelCollapsed)

  const [resolveOpen, setResolveOpen] = useState(false)
  const [resolveComment, setResolveComment] = useState('')
  const [resolving, setResolving] = useState(false)
  const [resolveConfirmationEnabled, setResolveConfirmationEnabled] = useState(false)
  const [pendingReasons, setPendingReasons] = useState<string[]>([])
  const [pendingCommentRequired, setPendingCommentRequired] = useState(true)
  const [pendingModalOpen, setPendingModalOpen] = useState(false)
  const [pendingReason, setPendingReason] = useState<string | null>(null)
  const [pendingComment, setPendingComment] = useState('')
  const [pendingSubmitting, setPendingSubmitting] = useState(false)
  const [pendingFlushDraft, setPendingFlushDraft] = useState(false)

  const [helpOpen, setHelpOpen] = useState(false)
  const [helpText, setHelpText] = useState('')
  const [helpSending, setHelpSending] = useState(false)

  const [noteEditing, setNoteEditing] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const [noteSaving, setNoteSaving] = useState(false)

  const [macros, setMacros] = useState<MacroRow[]>([])
  const {
    loadReplyFavorites,
    openFavoritesModal,
    openVariablesModal,
    replyFavorites,
    replyToolsModals,
  } = useTicketReplyTools(ticket?.project_id)

  const [projectDetail, setProjectDetail] = useState<ProjectDetailResponse | null>(null)
  const [customerIdentifierRows, setCustomerIdentifierRows] = useState<ProjectUserIdentifierRow[]>([])
  const [projectCustomerFields, setProjectCustomerFields] = useState<ProjectCustomerFieldRow[]>([])
  const [reviewStatus, setReviewStatus] = useState<TicketReviewStatusResponse | null>(null)
  const [productReviewStatus, setProductReviewStatus] = useState<TicketProductReviewStatusResponse | null>(null)
  const [avatarModalOpen, setAvatarModalOpen] = useState(false)
  const [identifiersModalOpen, setIdentifiersModalOpen] = useState(false)
  const [identNewType, setIdentNewType] = useState('email')
  const [identNewValue, setIdentNewValue] = useState('')
  const [identNewDisplay, setIdentNewDisplay] = useState('')
  const [identSaving, setIdentSaving] = useState(false)
  const [reviewBusy, setReviewBusy] = useState(false)
  const [productReviewBusy, setProductReviewBusy] = useState(false)

  const {
    aiInstructionModal,
    onSummary,
    openAiInstructionModal,
    showCreateInstructionBtn,
  } = useTicketAiInstruction({
    ticket,
    ticketId,
    userRole: user?.role,
  })

  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const scrollViewport = useRef<HTMLDivElement | null>(null)
  const replyAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const {
    handleReplyKeyDown,
    pickSlashItem,
    processSlashInReply,
    requestReplyCaretAt,
    resetReplySlash,
    slashMenuRef,
    slashUI,
  } = useTicketReplySlash({
    replyText,
    replyAreaRef,
    templates,
    macros,
    replyFavorites,
    setReplyText,
    setReplyTemplateId,
  })

  const {
    avatarResolvedSrc,
    canPostProductReview,
    customerHeadingName,
    displayIdentifiers,
    hasOtherProductRequests,
    isEmailChannel,
    pinnedMacros,
    pinnedTemplates,
    replyPanelMinHeight,
    showMyWorkReviewBlock,
    showProductReviewBlock,
    sidebarEmail,
    sortedProjectFields,
    tagsToAdd,
    ticketStatusSelectData,
    widgetEmailWait,
  } = useTicketDetailViewModel({
    customer,
    customerIdentifierRows,
    filesCount: files.length,
    isPhoneLayout,
    isTicketWideLayout,
    macros,
    pendingInstallersCount: pendingInstallers.length,
    productReviewStatus,
    projectCustomerFields,
    projectDetail,
    quotedMessage,
    replyFavorites,
    replyTarget,
    tags,
    templates,
    ticket,
  })

  const messageById = useMemo(() => new Map(messages.map((msg) => [msg.id, msg])), [messages])

  const appendThreadMessage = useCallback((raw: Record<string, unknown>) => {
    const msg = ticketDetailMessageFromApi(raw)
    if (!msg) return
    setMessages((prev) => {
      const next = mergeTicketMessages(prev, msg)
      if (next.length > prev.length) {
        setMessagesTotal((total) => Math.max(total, next.length))
      }
      return next
    })
  }, [])

  const initialLoad = useCallback(async () => {
    if (!ticketId) return
    const seq = ++threadRefreshSeq.current
    setKbPanelOpen(false)
    setSimilarPanelOpen(false)
    setLoading(true)
    setLoadError(null)
    try {
      const res = await api.ticketDetail(ticketId, { messagesLimit: MESSAGES_LIMIT, messagesOffset: 0 })
      if (seq !== threadRefreshSeq.current) return
      setTicket(res.ticket)
      setMessages(res.messages)
      setMessagesTotal(res.total)
      setMessagesOffset(0)

      const pid = res.ticket.project_id
      const cid = res.ticket.customer_id
      const setIfCurrent = <T,>(setter: (value: T) => void) => (value: T) => {
        if (seq === threadRefreshSeq.current) setter(value)
      }
      const runIfCurrent = (fn: () => void) => {
        if (seq === threadRefreshSeq.current) fn()
      }
      setLoading(false)

      void Promise.all([
        api.projects().then(setIfCurrent(setWorkspaceProjects)).catch(() => runIfCurrent(() => setWorkspaceProjects([]))),
        api.workspaceUsers().then(setIfCurrent(setUsers)).catch(() => runIfCurrent(() => setUsers([]))),
        api
          .workspaceMe()
          .then((ws) => {
            if (seq !== threadRefreshSeq.current) return
            setResolveConfirmationEnabled(Boolean(ws.resolve_confirmation_enabled))
            setRefundCalculatorEnabled(Boolean(ws.refund_calculator_enabled))
            setPendingReasons(
              Array.isArray(ws.pending_status_reasons)
                ? ws.pending_status_reasons.map((item) => String(item || '').trim()).filter(Boolean)
                : [],
            )
            setPendingCommentRequired(ws.pending_status_comment_required !== false)
          })
          .catch(() => {
            if (seq !== threadRefreshSeq.current) return
            setResolveConfirmationEnabled(false)
            setRefundCalculatorEnabled(false)
            setPendingReasons([])
            setPendingCommentRequired(true)
          }),
        api.ticketViewers(ticketId).then((r) => runIfCurrent(() => setViewers(r.viewers ?? []))).catch(() => runIfCurrent(() => setViewers([]))),
        pid
          ? api.projectTags(pid).then(setIfCurrent(setTags)).catch(() => runIfCurrent(() => setTags([])))
          : Promise.resolve(),
        pid
          ? api.projectTemplates(pid).then(setIfCurrent(setTemplates)).catch(() => runIfCurrent(() => setTemplates([])))
          : Promise.resolve(),
        pid
          ? api.projectMacros(pid).then(setIfCurrent(setMacros)).catch(() => runIfCurrent(() => setMacros([])))
          : Promise.resolve(),
        pid ? loadReplyFavorites(pid) : Promise.resolve(),
        pid
          ? api
              .project(pid)
              .then((p) => {
                if (seq !== threadRefreshSeq.current) return
                setProjectDetail(p)
                setRequireResolutionComment(Boolean(p.require_resolution_comment))
              })
              .catch(() => {
                if (seq !== threadRefreshSeq.current) return
                setProjectDetail(null)
                setRequireResolutionComment(false)
              })
          : Promise.resolve(),
        cid
          ? api.customer(cid).then(setIfCurrent(setCustomer)).catch(() => runIfCurrent(() => setCustomer(null)))
          : Promise.resolve(),
        cid
          ? loadCustomerTicketHistory(
              (limit, offset) => api.customerPreviousTickets(cid, ticketId, limit, undefined, offset),
              () => seq === threadRefreshSeq.current,
            )
              .then(setIfCurrent(setPreviousTickets))
              .catch(() => runIfCurrent(() => setPreviousTickets([])))
          : Promise.resolve(),
        pid && cid
          ? api
              .projectUserIdentifiers(pid, cid, { allProjects: true })
              .then(setIfCurrent(setCustomerIdentifierRows))
              .catch(() => runIfCurrent(() => setCustomerIdentifierRows([])))
          : Promise.resolve(),
        pid
          ? api.projectFields(pid).then(setIfCurrent(setProjectCustomerFields)).catch(() => runIfCurrent(() => setProjectCustomerFields([])))
          : Promise.resolve(),
        api
          .ticketReviewStatus(ticketId)
          .then(setIfCurrent(setReviewStatus))
          .catch(() => runIfCurrent(() => setReviewStatus({ requested: false, submitted_at: null, rating: null }))),
        api
          .ticketProductReviewStatus(ticketId)
          .then(setIfCurrent(setProductReviewStatus))
          .catch(() =>
            runIfCurrent(() => setProductReviewStatus({ requested: false, requested_at: null, other_requests: [] })),
          ),
      ])
      void api.ticketOpen(ticketId).catch(() => {})
    } catch (e) {
      if (seq !== threadRefreshSeq.current) return
      setLoadError(e instanceof ApiError ? e.message : 'Не удалось загрузить обращение')
      // Do not blank already-open ticket on soft failures (prevents "opens then disappears").
      // Only blank on hard 404 / invalid ref.
      if (e instanceof ApiError && e.status === 404) {
        setTicket(null)
      }
    } finally {
      if (seq === threadRefreshSeq.current) setLoading(false)
    }
  }, [loadReplyFavorites, ticketId])

  useEffect(() => {
    messagesOffsetRef.current = messagesOffset
  }, [messagesOffset])

  /**
   * Обновить тикет и ленту без полноэкранного loading.
   * @param opts.light — только detail+viewers (для fallback-poll), без review-status.
   */
  const refreshThreadSoft = useCallback(async (opts?: { light?: boolean }) => {
    if (!ticketId) return
    const seq = ++threadRefreshSeq.current
    setThreadRefreshing(true)
    try {
      const res = await api.ticketDetail(ticketId, { messagesLimit: MESSAGES_LIMIT, messagesOffset: 0 })
      if (seq !== threadRefreshSeq.current) return
      setTicket(res.ticket)
      setMessages(res.messages)
      setMessagesTotal(res.total)
      setMessagesOffset(0)
      const extras: Promise<unknown>[] = [
        api.ticketViewers(ticketId).then((r) => setViewers(r.viewers ?? [])).catch(() => setViewers([])),
      ]
      if (!opts?.light) {
        extras.push(
          api
            .ticketReviewStatus(ticketId)
            .then(setReviewStatus)
            .catch(() => setReviewStatus({ requested: false, submitted_at: null, rating: null })),
          api
            .ticketProductReviewStatus(ticketId)
            .then(setProductReviewStatus)
            .catch(() =>
              setProductReviewStatus({ requested: false, requested_at: null, other_requests: [] }),
            ),
        )
      }
      await Promise.all(extras)
    } catch {
      /* оставляем текущую переписку */
    } finally {
      if (seq === threadRefreshSeq.current) setThreadRefreshing(false)
    }
  }, [ticketId])

  useEffect(() => {
    void initialLoad()
  }, [initialLoad])

  /** Fallback: полный soft-refresh только если WS карточки не подключён. */
  useEffect(() => {
    if (!ticketId || loading) return
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return
      if (threadWsConnectedRef.current) return
      if (sendingRef.current || threadRefreshingRef.current) return
      if (messagesOffsetRef.current > 0) return
      void refreshThreadSoft({ light: true })
    }, THREAD_POLL_MS)
    return () => window.clearInterval(id)
  }, [ticketId, loading, refreshThreadSoft])

  /** WS карточки: новые сообщения и смена статуса доставки (pending → sent / failed). */
  useEffect(() => {
    if (!ticket?.id || loading) return
    const socketTicketId = ticket.id
    const wsUrl = getWebSocketUrl(`/api/v1/ws/ticket/${encodeURIComponent(socketTicketId)}`)
    let ws: WebSocket | null = null
    let closed = false
    let retryTimer: number | undefined
    threadWsConnectedRef.current = false

    const connect = () => {
      if (closed) return
      try {
        ws = new WebSocket(wsUrl)
      } catch {
        return
      }
      ws.onopen = () => {
        threadWsConnectedRef.current = true
      }
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data as string) as {
            type?: string
            message?: Record<string, unknown>
            status?: string
            assigned_to_id?: string | null
          }
          if (data.type === 'status_update' && typeof data.status === 'string') {
            setTicket((prev) => prev?.id === socketTicketId ? { ...prev, status: data.status! } : prev)
            return
          }
          if (data.type === 'assignee_update' && 'assigned_to_id' in data) {
            setTicket((prev) => prev?.id === socketTicketId ? { ...prev, assigned_to_id: data.assigned_to_id ?? null } : prev)
            return
          }
          const msg = data.message
          if (!msg?.id) return
          if (data.type === 'new_message' || data.type === 'message_updated') {
            if (data.type === 'new_message') appendThreadMessage(msg)
            else setMessages((prev) => mergeWsMessageIntoThread(prev, msg))
            if (
              data.type === 'message_updated' &&
              String(msg.channel_delivery_status || '') === 'failed' &&
              msg.delivery_error &&
              !deliveryFailureNotifiedRef.current.has(String(msg.id))
            ) {
              deliveryFailureNotifiedRef.current.add(String(msg.id))
              showNotification({
                title: 'Сообщение не отправлено',
                message: `${String(msg.delivery_error)}. Исправьте текст или дождитесь перевода и отправьте сообщение повторно.`,
                color: 'red',
                autoClose: 9000,
              })
            }
          }
        } catch {
          /* ignore */
        }
      }
      ws.onclose = () => {
        threadWsConnectedRef.current = false
        if (!closed) {
          retryTimer = window.setTimeout(connect, 5_000)
        }
      }
    }
    connect()
    return () => {
      closed = true
      threadWsConnectedRef.current = false
      if (retryTimer) window.clearTimeout(retryTimer)
      ws?.close()
    }
  }, [ticket?.id, loading, appendThreadMessage])

  useEffect(() => {
    if (!ticketId) return
    const onVis = () => {
      if (document.visibilityState !== 'visible') return
      if (sendingRef.current || threadRefreshingRef.current) return
      if (messagesOffsetRef.current > 0) return
      void refreshThreadSoft()
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [ticketId, refreshThreadSoft])

  useEffect(() => {
    setQuotedMessage(null)
    setReplyTemplateId(null)
    setReplyText(ticketId ? readTicketReplyDraft(ticketId) : '')
    resetReplySlash()
    setPreviousTicketsModalOpen(false)
  }, [resetReplySlash, ticketId])

  useEffect(() => {
    if (!ticketId) return
    if (replyText.trim()) writeTicketReplyDraft(ticketId, replyText)
    else removeTicketReplyDraft(ticketId)
  }, [ticketId, replyText])

  /** При открытии обращения и после полной перезагрузки — чат внизу (не трогаем после «Загрузить раньше»). */
  useLayoutEffect(() => {
    if (loading || !ticket?.id || messagesOffset !== 0) return
    if (isPhoneLayout) {
      const toEnd = () => messagesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' })
      toEnd()
      requestAnimationFrame(() => {
        toEnd()
        requestAnimationFrame(toEnd)
      })
      return
    }
    const vp = scrollViewport.current
    if (!vp) return
    const scrollBottom = () => {
      vp.scrollTop = vp.scrollHeight
    }
    scrollBottom()
    requestAnimationFrame(() => {
      scrollBottom()
      requestAnimationFrame(scrollBottom)
    })
  }, [loading, ticket?.id, messagesOffset, messages.length, isPhoneLayout])

  useEffect(() => {
    if (!ticketId) return
    const t = window.setInterval(() => {
      void api.ticketOpen(ticketId).catch(() => {})
    }, OPEN_HEARTBEAT_MS)
    return () => clearInterval(t)
  }, [ticketId])

  useEffect(() => {
    if (!ticketId) return
    const t = window.setInterval(() => {
      void api
        .ticketViewers(ticketId)
        .then((r) => setViewers(r.viewers ?? []))
        .catch(() => {})
    }, VIEWER_POLL_MS)
    return () => clearInterval(t)
  }, [ticketId])

  useEffect(() => {
    if (!ticket) return
    const t = ticketWorkspaceTabTitle(
      ticket.project_slug,
      ticket.ticket_number,
      ticket.subject_translated || ticket.subject,
    )
    updateActiveTabTitle(t)
    document.title = t
  }, [ticket, updateActiveTabTitle])

  const sdAdminActions = useTicketSdAdminActions({
    ticketId,
    refreshThreadSoft,
  })

  const {
    changeReplyLocale,
    onAddTag,
    onAssign,
    onPending,
    onPriority,
    onRemoveTag,
    onStatus,
  } = useTicketManagementActions({
    ticket,
    ticketId,
    setTicket,
  })

  const navigateToTicketsList = useCallback(() => {
    let p = '/tickets'
    try {
      p = sessionStorage.getItem(TICKETS_LIST_RETURN_URL_KEY) || '/tickets'
    } catch {
      /* ignore */
    }
    if (!p.startsWith('/')) p = `/${p}`
    navigate(p)
  }, [navigate])

  /** Как classic: закрыть вкладку карточки и перейти на сохранённый URL списка. */
  const leaveToTicketsListClosingTab = useCallback(() => {
    let targetPath = '/tickets'
    try {
      targetPath = sessionStorage.getItem(TICKETS_LIST_RETURN_URL_KEY) || '/tickets'
    } catch {
      /* ignore */
    }
    if (!targetPath.startsWith('/')) targetPath = `/${targetPath}`
    if (tabs.length <= 1) {
      navigate(targetPath)
      return
    }
    closeTab(activeTabId)
    navigate(targetPath)
  }, [tabs.length, activeTabId, closeTab, navigate])

  const loadOlder = useCallback(async () => {
    if (!ticketId || messages.length >= messagesTotal) return
    try {
      const nextOffset = messagesOffset + MESSAGES_LIMIT
      const res = await api.ticketDetail(ticketId, {
        messagesLimit: MESSAGES_LIMIT,
        messagesOffset: nextOffset,
      })
      setMessages((prev) => [...res.messages, ...prev])
      setMessagesOffset(nextOffset)
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось подгрузить сообщения',
        color: 'red',
      })
    }
  }, [ticketId, messages.length, messagesTotal, messagesOffset])

  /** Отправить текущий черновик; `isInternal` — режим «клиент / внутренняя». */
  const postCurrentReplyAsMessage = useCallback(
    async (isInternal: boolean, opts?: { send_reply_to_widget_email?: boolean }) => {
      if (!ticketId || !ticket) return
      const text = replyText.trim()
      if (!text && files.length === 0 && pendingInstallers.length === 0) return
      if (ticket.status === 'closed') {
        showNotification({ title: 'Закрыто', message: 'В закрытое обращение нельзя писать.', color: 'yellow' })
        throw new Error('closed')
      }
      const sendWidgetMail = !isInternal && !!opts?.send_reply_to_widget_email
      if (sendWidgetMail && (files.length > 0 || pendingInstallers.length > 0)) {
        showNotification({
          title: 'Ответ на почту',
          message: 'Сначала уберите вложения и установщики — письмо уходит только текстом.',
          color: 'yellow',
        })
        throw new Error('widget-email-attachments')
      }
      if (files.length > 0 || pendingInstallers.length > 0) {
        const fd = new FormData()
        fd.append('ticket_id', ticketId)
        fd.append('content', text)
        fd.append('message_type', 'operator')
        fd.append('is_internal', isInternal ? 'true' : 'false')
        if (quotedMessage?.id) fd.append('quote_message_id', quotedMessage.id)
        if (replyTemplateId) fd.append('template_id', replyTemplateId)
        for (const f of files) fd.append('files', f)
        if (pendingInstallers.length > 0) {
          fd.append('installer_ids', JSON.stringify(pendingInstallers.map((i) => i.id)))
        }
        if (sendWidgetMail) fd.append('send_reply_to_widget_email', 'true')
        const createdFiles = (await api.messageCreateWithFiles(fd)) as Record<string, unknown>
        appendThreadMessage(createdFiles)
      } else {
        const created = (await api.messageCreate({
          ticket_id: ticketId,
          content: text,
          message_type: 'operator',
          is_internal: isInternal,
          ...(quotedMessage?.id ? { quote_message_id: quotedMessage.id } : {}),
          ...(replyTemplateId ? { template_id: replyTemplateId } : {}),
          ...(sendWidgetMail ? { send_reply_to_widget_email: true } : {}),
        })) as Record<string, unknown>
        if (created.quote_message_id && !created.quoted_message && quotedMessage) {
          created.quoted_message = {
            id: quotedMessage.id,
            content: quotedMessage.content,
            sender_name: quotedMessage.sender_name,
            sender_email: quotedMessage.sender_email,
            message_type: quotedMessage.message_type,
            created_at: quotedMessage.created_at,
          }
        }
        appendThreadMessage(created)
      }
      setReplyText('')
      removeTicketReplyDraft(ticketId)
      resetReplyAttachments()
      setQuotedMessage(null)
      setReplyTemplateId(null)
    },
    [
      ticketId,
      ticket,
      replyText,
      files,
      pendingInstallers,
      quotedMessage,
      replyTemplateId,
      appendThreadMessage,
      resetReplyAttachments,
    ],
  )

  type SendPostAction = 'none' | 'next' | 'next-resolve' | 'list'

  const sendReplyWithOptions = useCallback(
    async (postAction: SendPostAction) => {
      if (!ticketId || !ticket?.project_id) return
      const textSnapshot = replyText.trim()
      if (!textSnapshot && files.length === 0 && pendingInstallers.length === 0) {
        showNotification({
          title: 'Пусто',
          message: 'Введите текст, прикрепите файл или установщик.',
          color: 'yellow',
        })
        return
      }
      if (ticket.status === 'closed') {
        showNotification({ title: 'Закрыто', message: 'В закрытое обращение нельзя писать.', color: 'yellow' })
        return
      }
      const isInternal = replyTarget === 'internal'
      setSending(true)
      try {
        await postCurrentReplyAsMessage(isInternal)
        await refreshThreadSoft()

        if (postAction === 'next-resolve') {
          await api.ticketUpdate(
            ticketId,
            requireResolutionComment
              ? {
                  status: 'resolved',
                  resolution_comment: textSnapshot,
                  send_resolution_notification: true,
                }
              : { status: 'resolved' },
          )
          await refreshThreadSoft()
        }

        if (postAction === 'next' || postAction === 'next-resolve') {
          const next = await fetchNextUnassignedTicketWide(ticket.project_id, ticketId)
          if (next) {
            const ref = ticketPathSegmentFromListItem(next)
            navigate(`/tickets/${encodeURIComponent(ref)}`)
            showNotification({
              title: postAction === 'next-resolve' ? 'Готово' : 'Отправлено',
              message:
                postAction === 'next-resolve'
                  ? 'Сообщение отправлено, обращение решено.'
                  : 'Сообщение отправлено.',
              color: 'green',
            })
          } else {
            showNotification({
              title: postAction === 'next-resolve' ? 'Готово' : 'Отправлено',
              message: 'Следующих обращений не найдено.',
              color: 'blue',
            })
          }
        } else if (postAction === 'list') {
          navigateToTicketsList()
          showNotification({ title: 'Отправлено', message: 'Сообщение отправлено.', color: 'green' })
        } else {
          showNotification({ title: 'Отправлено', message: 'Сообщение добавлено.', color: 'green' })
        }
        if (postAction !== 'none') notifyTicketsWorkspaceStale()
      } catch (e) {
        if ((e as Error)?.message === 'closed') return
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось отправить',
          color: 'red',
        })
      } finally {
        setSending(false)
      }
    },
    [
      ticketId,
      ticket,
      replyText,
      files,
      pendingInstallers,
      replyTarget,
      postCurrentReplyAsMessage,
      refreshThreadSoft,
      requireResolutionComment,
      navigate,
      navigateToTicketsList,
    ],
  )

  const sendReply = useCallback(() => {
    void sendReplyWithOptions('none')
  }, [sendReplyWithOptions])

  /** Виджет: ответ клиенту дублируем на указанную в диалоге почту (SMTP email-канал проекта). */
  const sendReplyToWidgetMailbox = useCallback(async () => {
    if (!ticketId || !ticket) return
    if (replyTarget !== 'customer') {
      showNotification({ title: 'Режим', message: 'Переключитесь на «Ответ пользователю».', color: 'yellow' })
      return
    }
    if (!replyText.trim()) {
      showNotification({ title: 'Пусто', message: 'Введите текст ответа для отправки на почту.', color: 'yellow' })
      return
    }
    if (ticket.status === 'closed') {
      showNotification({ title: 'Закрыто', message: 'В закрытое обращение нельзя писать.', color: 'yellow' })
      return
    }
    setSending(true)
    try {
      await postCurrentReplyAsMessage(false, { send_reply_to_widget_email: true })
      await refreshThreadSoft()
      showNotification({ title: 'В очереди', message: 'Текст отправлен на почту клиента (если настроен email-канал).', color: 'green' })
    } catch (e) {
      if (e instanceof Error && e.message === 'widget-email-attachments') return
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось отправить на почту',
        color: 'red',
      })
    } finally {
      setSending(false)
    }
  }, [ticketId, ticket, replyTarget, replyText, postCurrentReplyAsMessage, refreshThreadSoft])

  /** Ctrl+Alt+S: как classic — в клиента (не внутренняя), при необходимости отправить, затем resolved и остаться. */
  const handleSendAndResolveStay = useCallback(async () => {
    if (!ticketId || !ticket) return
    if (ticket.status === 'closed' || ticket.status === 'resolved') return
    try {
      if (replyText.trim() || files.length > 0 || pendingInstallers.length > 0) {
        await postCurrentReplyAsMessage(false)
      }
      await api.ticketUpdate(ticketId, { status: 'resolved' })
      await refreshThreadSoft()
      notifyTicketsWorkspaceStale()
      showNotification({ title: 'Решено', message: 'Обращение отмечено как решённое.', color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось выполнить действие',
        color: 'red',
      })
    }
  }, [
    ticketId,
    ticket,
    replyText,
    files,
    pendingInstallers,
    postCurrentReplyAsMessage,
    refreshThreadSoft,
  ])

  const handleNextOpenTicket = useCallback(async () => {
    if (!ticketId || !ticket?.project_id) return
    try {
      const next = await fetchNextUnassignedOpenTicket(ticket.project_id, ticketId)
      if (next) {
        const ref = ticketPathSegmentFromListItem(next)
        navigate(`/tickets/${encodeURIComponent(ref)}`)
      } else {
        showNotification({
          title: 'Нет обращений',
          message: 'Следующих открытых обращений в этом проекте не найдено.',
          color: 'blue',
        })
      }
    } catch {
      showNotification({
        title: 'Ошибка',
        message: 'Не удалось перейти к следующему обращению.',
        color: 'red',
      })
    }
  }, [ticketId, ticket?.project_id, navigate])

  /** Как classic: Ctrl+Alt+S — отправить (в клиента) и решить, остаться; Ctrl+Alt+↓ — следующее открытое. */
  useEffect(() => {
    if (!ticketId || !ticket) return
    const hotkeyHandler = (e: KeyboardEvent) => {
      if (!e.ctrlKey || !e.altKey) return
      const el = e.target as HTMLElement | null
      if (el?.closest?.('[role="dialog"]')) return
      if (e.key === 's' || e.key === 'S') {
        e.preventDefault()
        void handleSendAndResolveStay()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        void handleNextOpenTicket()
      }
    }
    document.addEventListener('keydown', hotkeyHandler)
    return () => document.removeEventListener('keydown', hotkeyHandler)
  }, [ticketId, ticket, handleSendAndResolveStay, handleNextOpenTicket])

  // Все вложения клиента (по всем его обращениям) — ленивая загрузка при открытии панели.
  const openAttachmentsModal = useCallback(() => {
    setAttachmentsModalOpen(true)
    const cid = ticket?.customer_id
    if (!cid || attachmentsLoaded || attachmentsLoading) return
    setAttachmentsLoading(true)
    api
      .customerAttachments(cid)
      .then((rows) => {
        setCustomerAttachments(rows)
        setAttachmentsLoaded(true)
      })
      .catch(() => {
        setCustomerAttachments([])
        showNotification({
          title: 'Ошибка',
          message: 'Не удалось загрузить вложения клиента.',
          color: 'red',
        })
      })
      .finally(() => setAttachmentsLoading(false))
  }, [ticket?.customer_id, attachmentsLoaded, attachmentsLoading])

  // При смене клиента/обращения сбрасываем кэш вложений, чтобы подгрузить заново.
  useEffect(() => {
    setCustomerAttachments([])
    setAttachmentsLoaded(false)
    setAttachmentsLoading(false)
  }, [ticket?.customer_id])

  const submitResolve = useCallback(async () => {
    if (!ticketId) return
    const c = resolveComment.trim()
    if (requireResolutionComment && !c) {
      showNotification({ title: 'Комментарий', message: 'Укажите комментарий к решению.', color: 'yellow' })
      return
    }
    setResolving(true)
    try {
      if (replyText.trim() || files.length > 0 || pendingInstallers.length > 0) {
        await postCurrentReplyAsMessage(false)
      }
      await api.ticketUpdate(
        ticketId,
        requireResolutionComment
          ? { status: 'resolved', resolution_comment: c, send_resolution_notification: true }
          : { status: 'resolved' },
      )
      setResolveOpen(false)
      setResolveComment('')
      notifyTicketsWorkspaceStale()
      leaveToTicketsListClosingTab()
      showNotification({ title: 'Решено', message: 'Обращение решено.', color: 'green' })
    } catch (e) {
      if ((e as Error)?.message !== 'closed') {
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось решить обращение',
          color: 'red',
        })
      }
    } finally {
      setResolving(false)
    }
  }, [
    ticketId,
    requireResolutionComment,
    resolveComment,
    replyText,
    files,
    pendingInstallers,
    postCurrentReplyAsMessage,
    leaveToTicketsListClosingTab,
  ])

  /** Кнопка «Решить» без обязательного комментария — сразу, как в classic. */
  const resolveTicketAndGoToList = useCallback(async () => {
    if (!ticketId || !ticket) return
    if (ticket.status === 'closed' || ticket.status === 'resolved') return
    if (requireResolutionComment) {
      setResolveComment(replyText.trim())
      setResolveOpen(true)
      return
    }
    setResolving(true)
    try {
      if (replyText.trim() || files.length > 0 || pendingInstallers.length > 0) {
        await postCurrentReplyAsMessage(false)
      }
      await api.ticketUpdate(ticketId, { status: 'resolved' })
      notifyTicketsWorkspaceStale()
      leaveToTicketsListClosingTab()
      showNotification({ title: 'Решено', message: 'Обращение решено.', color: 'green' })
    } catch (e) {
      if ((e as Error)?.message !== 'closed') {
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось решить обращение',
          color: 'red',
        })
      }
    } finally {
      setResolving(false)
    }
  }, [
    ticketId,
    ticket,
    requireResolutionComment,
    replyText,
    files,
    pendingInstallers,
    postCurrentReplyAsMessage,
    leaveToTicketsListClosingTab,
  ])

  /**
   * Смена статуса из бокового «Управление» (селект статуса, кнопка «Закрыть») —
   * в отличие от кнопки «Решить» в composer, эти пути не флашили черновик перед
   * сменой статуса, и он молча оставался неотправленным (в т.ч. в виджете).
   */
  const changeStatusWithDraftFlush = useCallback(
    async (next: string) => {
      if (!ticketId || !ticket) return
      if (next === 'pending') {
        setPendingFlushDraft(true)
        setPendingReason(pendingReasons[0] || null)
        setPendingComment('')
        setPendingModalOpen(true)
        return
      }
      if (ticket.status !== 'closed' && (replyText.trim() || files.length > 0 || pendingInstallers.length > 0)) {
        try {
          await postCurrentReplyAsMessage(false)
        } catch (e) {
          if ((e as Error)?.message === 'closed') return
          showNotification({
            title: 'Ошибка',
            message: e instanceof ApiError ? e.message : 'Не удалось отправить сообщение',
            color: 'red',
          })
          return
        }
      }
      await onStatus(next)
    },
    [ticketId, ticket, pendingReasons, replyText, files, pendingInstallers, postCurrentReplyAsMessage, onStatus],
  )

  const openPendingModal = useCallback(
    (flushDraft: boolean) => {
      setPendingFlushDraft(flushDraft)
      setPendingReason(pendingReasons[0] || null)
      setPendingComment('')
      setPendingModalOpen(true)
    },
    [pendingReasons],
  )

  const submitPendingStatus = useCallback(async () => {
    if (!ticketId || !ticket) return
    const reason = String(pendingReason || '').trim()
    const comment = pendingComment.trim()
    if (!reason) {
      showNotification({ title: 'Ожидание', message: 'Выберите причину ожидания', color: 'yellow' })
      return
    }
    if (pendingCommentRequired && !comment) {
      showNotification({ title: 'Ожидание', message: 'Напишите комментарий', color: 'yellow' })
      return
    }
    setPendingSubmitting(true)
    try {
      if (
        pendingFlushDraft &&
        ticket.status !== 'closed' &&
        (replyText.trim() || files.length > 0 || pendingInstallers.length > 0)
      ) {
        await postCurrentReplyAsMessage(false)
      }
      await onPending({ reason, comment })
      setPendingModalOpen(false)
    } catch (e) {
      if ((e as Error)?.message === 'closed') return
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось перевести обращение в ожидание',
        color: 'red',
      })
    } finally {
      setPendingSubmitting(false)
    }
  }, [
    files.length,
    onPending,
    pendingComment,
    pendingCommentRequired,
    pendingFlushDraft,
    pendingInstallers.length,
    pendingReason,
    postCurrentReplyAsMessage,
    replyText,
    ticket,
    ticketId,
  ])

  const {
    onDeleteIdentifier,
    submitNewIdentifier,
  } = useTicketCustomerIdentifiers({
    ticket,
    identifiersModalOpen,
    newType: identNewType,
    newValue: identNewValue,
    newDisplay: identNewDisplay,
    setNewValue: setIdentNewValue,
    setNewDisplay: setIdentNewDisplay,
    setSaving: setIdentSaving,
    setCustomer,
    setRows: setCustomerIdentifierRows,
    refreshThreadSoft,
  })

  const copyToClipboard = useCallback(async (text: string, okMessage = 'Скопировано') => {
    const t = text.trim()
    if (!t) return
    try {
      await navigator.clipboard.writeText(t)
      showNotification({ title: okMessage, message: t.slice(0, 120), color: 'green' })
    } catch {
      showNotification({ title: 'Копирование', message: 'Не удалось скопировать в буфер.', color: 'red' })
    }
  }, [])

  const onRequestReview = useCallback(async () => {
    if (!ticketId) return
    setReviewBusy(true)
    try {
      await api.requestTicketReview(ticketId)
      const s = await api.ticketReviewStatus(ticketId)
      setReviewStatus(s)
      showNotification({ title: 'Запрос', message: 'Запрос оценки отправлен.', color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось запросить оценку',
        color: 'red',
      })
    } finally {
      setReviewBusy(false)
    }
  }, [ticketId])

  const onRequestProductReview = useCallback(async () => {
    if (!ticketId) return
    setProductReviewBusy(true)
    try {
      const result = await api.requestTicketProductReview(ticketId, ['all'])
      const s = await api.ticketProductReviewStatus(ticketId)
      setProductReviewStatus(s)
      if (result.delivery_status === 'failed' || result.delivery_status === 'skipped') {
        showNotification({
          title: 'Запрос создан',
          message:
            result.delivery_status === 'skipped'
              ? 'Сообщение добавлено в обращение, но доставка в канал пропущена: не хватает данных клиента.'
              : 'Сообщение добавлено в обращение, но доставка в канал не прошла.',
          color: 'yellow',
        })
      } else {
        showNotification({
          title: 'Запрос',
          message: 'Запрос отзыва о продукте отправлен.',
          color: 'green',
        })
      }
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось запросить отзыв',
        color: 'red',
      })
    } finally {
      setProductReviewBusy(false)
    }
  }, [ticketId])

  const enabledSdAdminActions = useMemo(() => {
    const configured = projectDetail?.sd_admin_api_actions || {}
    return SD_ADMIN_API_ACTIONS.filter((item) => {
      const value = configured[item.key]
      const objectValue = value && typeof value === 'object' && !Array.isArray(value) ? value as { enabled?: boolean } : null
      return value === true || objectValue?.enabled === true
    })
  }, [projectDetail?.sd_admin_api_actions])

  const onToggleProductReviewReceived = useCallback(
    async (nextChecked: boolean) => {
      if (!productReviewStatus?.id || productReviewBusy) return
      setProductReviewBusy(true)
      try {
        const r = await api.setProductReviewReceived(productReviewStatus.id, nextChecked)
        setProductReviewStatus((prev) =>
          prev
            ? {
                ...prev,
                review_received: r.review_received,
                review_received_at: r.review_received_at ?? null,
              }
            : prev,
        )
      } catch (e) {
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось обновить статус отзыва',
          color: 'red',
        })
      } finally {
        setProductReviewBusy(false)
      }
    },
    [productReviewStatus, productReviewBusy],
  )

  const submitHelp = useCallback(async () => {
    if (!ticketId) return
    const m = helpText.trim()
    if (!m) {
      showNotification({ title: 'Текст', message: 'Введите сообщение для коллег.', color: 'yellow' })
      return
    }
    setHelpSending(true)
    try {
      await api.requestTicketHelp(ticketId, m)
      setHelpOpen(false)
      setHelpText('')
      showNotification({ title: 'Помощь', message: 'Запрос отправлен.', color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось отправить запрос',
        color: 'red',
      })
    } finally {
      setHelpSending(false)
    }
  }, [ticketId, helpText])

  const saveNote = useCallback(async () => {
    if (!customer?.id) return
    setNoteSaving(true)
    try {
      await api.customerUpdate(customer.id, { note: noteDraft.trim() || null })
      setCustomer((c) => (c ? { ...c, note: noteDraft.trim() || null } : c))
      setNoteEditing(false)
      showNotification({ title: 'Примечание', message: 'Сохранено.', color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setNoteSaving(false)
    }
  }, [customer?.id, noteDraft])

  const { openTicketInNewTab } = useTicketHeaderControls({ ticket, copyToClipboard })

  const {
    displayedPreviousTickets,
    onSelectPreviousTicket,
    openPreviousTicketInNewTab,
    previousTicketsProjectCounts,
    previousTicketTabStateById,
    projectPickForPreviousList,
    sortedPreviousTickets,
  } = useTicketPreviousTickets({
    currentTicket: ticket,
    previousTickets,
    workspaceProjects,
    tabs,
    activeTabId,
    previewLimit: PREVIOUS_TICKETS_SIDEBAR_PREVIEW,
    openOrSelectTab,
    closePreviousTicketsModal: () => setPreviousTicketsModalOpen(false),
  })

  const { parallelBanner, goToEarliest, dismissParallelBanner } = useParallelCustomerTickets({
    ticketId: ticket?.id,
    openOrSelectTab,
  })

  const {
    assignmentTimeline,
    openTimeline,
    timelineModal,
  } = useTicketTimeline({
    ticketId,
    assignedToId: ticket?.assigned_to_id,
    messages,
  })

  const setReplyCollapsedPersist = useCallback((collapsed: boolean) => {
    setReplyPanelCollapsed(collapsed)
    try {
      localStorage.setItem(REPLY_PANEL_LS_COLLAPSED, collapsed ? '1' : '0')
    } catch {
      /* ignore */
    }
  }, [])

  const handleQuoteMessage = useCallback(
    (msg: TicketDetailMessage) => {
      setQuotedMessage(msg)
      setReplyCollapsedPersist(false)
      queueMicrotask(() => {
        replyAreaRef.current?.focus()
      })
    },
    [setReplyCollapsedPersist],
  )

  const {
    assigneeOptions,
    assigneeUserById,
    assigneeFilter,
    renderAssigneeOption,
  } = useTicketAssigneeSelect({
    users,
    assignedToId: ticket?.assigned_to_id,
    visibleRoles: assigneeVisibleRoles,
    isUserOnShift,
  })

  const applyReplyTemplate = useCallback((tpl: ProjectTemplateRow) => {
    const content = tpl.content || ''
    const next = content && !content.endsWith('\n') ? `${content}\n` : content
    setReplyText(next)
    setReplyTemplateId(tpl.id)
    resetReplySlash()
    requestReplyCaretAt(next.length)
  }, [requestReplyCaretAt, resetReplySlash])

  const insertAtCursor = useCallback(
    (insert: string, options?: { withTrailingNewline?: boolean }) => {
      setReplyTemplateId(null)
      const ta = replyAreaRef.current
      const finalInsert =
        options?.withTrailingNewline && insert && !insert.endsWith('\n') ? `${insert}\n` : insert
      if (!ta) {
        setReplyText((prev) => prev + finalInsert)
        return
      }
      const start = ta.selectionStart ?? replyText.length
      const end = ta.selectionEnd ?? start
      const next = replyText.slice(0, start) + finalInsert + replyText.slice(end)
      setReplyText(next)
      const pos = start + finalInsert.length
      requestReplyCaretAt(pos)
    },
    [replyText, requestReplyCaretAt],
  )

  if (!ticketId) {
    return (
      <PageShell>
        <Stack gap="sm">
          <OnBreakWorkBanner />
          <Alert color="red">Некорректная ссылка на обращение.</Alert>
        </Stack>
      </PageShell>
    )
  }

  if (loading && !ticket) {
    return (
      <PageShell>
        <Stack gap="sm">
          <OnBreakWorkBanner />
          <PageCenterLoader label="Загрузка обращения" minHeight={320} />
        </Stack>
      </PageShell>
    )
  }

  if (loadError || !ticket) {
    return (
      <PageShell>
        <Stack gap="sm">
          <OnBreakWorkBanner />
          <Alert color="red" title="Ошибка">
            {loadError || 'Обращение не найдено'}
          </Alert>
        </Stack>
      </PageShell>
    )
  }

  return (
    <PageShell variant="fill">
      <Stack
        gap="xs"
        style={{
          flex: '1 1 0%',
          minHeight: 0,
          height: '100%',
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <OnBreakWorkBanner />
        {parallelBanner ? (
          <ParallelCustomerTicketsBanner
            data={parallelBanner}
            onGoToEarliest={goToEarliest}
            onDismiss={dismissParallelBanner}
          />
        ) : null}
        <TicketTimelineModal
          opened={timelineModal.opened}
          loading={timelineModal.loading}
          error={timelineModal.error}
          timeline={timelineModal.timeline}
          fromId={timelineModal.fromId}
          toId={timelineModal.toId}
          selectedIds={timelineModal.selectedIds}
          selectedKind={timelineModal.selectedKind}
          onClose={timelineModal.onClose}
          onSelectItems={timelineModal.onSelectItems}
          onResetRange={timelineModal.onResetRange}
          onPick={timelineModal.onPick}
        />
        <Flex
          direction={isTicketWideLayout ? 'row' : 'column'}
          align="stretch"
          wrap="nowrap"
          gap="xs"
          w="100%"
          style={{
            flex: '1 1 0%',
            minHeight: 0,
            height: '100%',
            minWidth: 0,
            alignItems: 'stretch',
            /** На телефоне одна прокрутка: сначала переписка + ответ, ниже — правый блок */
            overflow: isPhoneLayout ? 'auto' : 'hidden',
            overflowX: 'hidden',
            WebkitOverflowScrolling: isPhoneLayout ? 'touch' : undefined,
          }}
        >
          <Box
            style={{
              flex: isTicketWideLayout ? '1 1 420px' : isPhoneLayout ? '0 0 auto' : '1 1 0%',
              minWidth: isTicketWideLayout ? 260 : 0,
              minHeight: 0,
              height: isTicketWideLayout ? '100%' : undefined,
              maxHeight: isTicketWideLayout ? '100%' : undefined,
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--mantine-spacing-xs)',
              overflow: isPhoneLayout ? 'visible' : !isTicketWideLayout ? 'auto' : 'hidden',
              overflowX: 'hidden',
            }}
          >
            <TicketMessagesPanel
              ticket={ticket}
              messages={messages}
              messagesTotal={messagesTotal}
              visibleMessages={assignmentTimeline.visibleMessages}
              messageById={messageById}
              users={users}
              currentUserId={user?.id}
              tags={tags}
              tagsToAdd={tagsToAdd}
              isPhoneLayout={isPhoneLayout}
              isTicketWideLayout={isTicketWideLayout}
              loading={loading}
              threadRefreshing={threadRefreshing}
              timelineLoading={timelineModal.loading}
              showAssignmentSystemMessages={assignmentTimeline.showSystemMessages}
              assignmentTimelineLoading={assignmentTimeline.loading}
              assignmentTimelineError={assignmentTimeline.error}
              assignmentTimelineItems={assignmentTimeline.items}
              widgetEmailWait={widgetEmailWait}
              scrollViewport={scrollViewport}
              messagesEndRef={messagesEndRef}
              onRemoveTag={onRemoveTag}
              onAddTag={onAddTag}
              onToggleSystemMessages={assignmentTimeline.toggleSystemMessages}
              onSpamChange={(state) =>
                setTicket((prev) =>
                  prev?.id === state.id
                    ? {
                        ...prev,
                        status: state.status,
                        is_spam: state.is_spam,
                        spam_source: state.spam_source ?? null,
                        spam_reason: state.spam_reason ?? null,
                        spam_marked_at: state.spam_marked_at ?? null,
                      }
                    : prev,
                )
              }
              onOpenTimeline={() => void openTimeline()}
              onOpenTicketInNewTab={openTicketInNewTab}
              onRefreshThread={() => void refreshThreadSoft()}
              onLoadOlder={() => void loadOlder()}
              onQuoteMessage={handleQuoteMessage}
            />

            <TicketReplyComposer
              ticket={ticket}
              collapsed={replyPanelCollapsed}
              minHeight={replyPanelMinHeight}
              isPhoneLayout={isPhoneLayout}
              isTicketWideLayout={isTicketWideLayout}
              replyTarget={replyTarget}
              replyText={replyText}
              quotedMessage={quotedMessage}
              pinnedTemplates={pinnedTemplates}
              pinnedMacros={pinnedMacros}
              templates={templates}
              macros={macros}
              files={files}
              pendingInstallers={pendingInstallers}
              pendingInstallerIds={pendingInstallerIds}
              replyImageBlobUrls={replyImageBlobUrls}
              refundCalculatorEnabled={refundCalculatorEnabled}
              showCreateInstructionBtn={showCreateInstructionBtn}
              sending={sending}
              resolving={resolving}
              requireResolutionComment={requireResolutionComment}
              resolveConfirmationEnabled={resolveConfirmationEnabled}
              widgetEmailWait={widgetEmailWait}
              canViewKnowledgeBase={canViewKnowledgeBase}
              kbPanelOpen={kbPanelOpen}
              similarPanelOpen={similarPanelOpen}
              replyAreaRef={replyAreaRef}
              replyFileIconResetRef={replyFileIconResetRef}
              onSetCollapsed={setReplyCollapsedPersist}
              onChangeReplyTarget={setReplyTarget}
              onOpenHelp={() => setHelpOpen(true)}
              onOpenAiInstruction={openAiInstructionModal}
              onApplyTemplate={applyReplyTemplate}
              onInsertText={insertAtCursor}
              onClearQuote={() => setQuotedMessage(null)}
              onChangeReplyText={setReplyText}
              onProcessSlash={processSlashInReply}
              onReplyKeyDown={handleReplyKeyDown}
              onPickInstaller={addPendingInstaller}
              onOpenRefundCalculator={() => setRefundCalculatorOpen(true)}
              onToggleKnowledgeBase={() => {
                setSimilarPanelOpen(false)
                setKbPanelOpen((value) => !value)
              }}
              onToggleSimilarClosedTickets={() => {
                setKbPanelOpen(false)
                setSimilarPanelOpen((value) => !value)
              }}
              onAppendFiles={appendReplyFiles}
              onRemovePendingInstallerAt={removePendingInstallerAt}
              onRemoveReplyFileAt={removeReplyFileAt}
              onSummary={() => void onSummary()}
              onOpenVariables={openVariablesModal}
              onOpenFavorites={openFavoritesModal}
              onPending={() => openPendingModal(false)}
              onNextOpenTicket={() => void handleNextOpenTicket()}
              onSendReply={sendReply}
              onSendReplyToWidgetMailbox={() => void sendReplyToWidgetMailbox()}
              onSendReplyWithOptions={(option) => void sendReplyWithOptions(option)}
              onOpenResolveWithComment={(comment) => {
                setResolveComment(comment)
                setResolveOpen(true)
              }}
              onResolveTicketAndGoToList={() => void resolveTicketAndGoToList()}
              onCloseTicket={() => void changeStatusWithDraftFlush('closed')}
            />
          </Box>

          <TicketSidebarPanel
            ticket={ticket}
            currentUser={user}
            isPhoneLayout={isPhoneLayout}
            isTicketWideLayout={isTicketWideLayout}
            classicBadgeDark={classicBadgeDark}
            statusOptions={ticketStatusSelectData}
            assigneeOptions={assigneeOptions}
            assigneeUserById={assigneeUserById}
            assigneeFilter={assigneeFilter}
            renderAssigneeOption={renderAssigneeOption}
            onStatus={(next) => void changeStatusWithDraftFlush(next)}
            onPriority={(next) => void onPriority(next)}
            onAssign={(next) => void onAssign(next)}
            onChangeReplyLocale={changeReplyLocale}
            showMyWorkReview={showMyWorkReviewBlock}
            showProductReview={showProductReviewBlock}
            isEmailChannel={isEmailChannel}
            reviewStatus={reviewStatus}
            productReviewStatus={productReviewStatus}
            hasOtherProductRequests={hasOtherProductRequests}
            canPostProductReview={canPostProductReview}
            reviewBusy={reviewBusy}
            productReviewBusy={productReviewBusy}
            onRequestReview={() => void onRequestReview()}
            onRequestProductReview={() => void onRequestProductReview()}
            onToggleProductReviewReceived={(received) => void onToggleProductReviewReceived(received)}
            viewers={viewers}
            customer={customer}
            avatarSrc={avatarResolvedSrc}
            customerName={customerHeadingName}
            sidebarEmail={sidebarEmail}
            identifiers={displayIdentifiers}
            noteEditing={noteEditing}
            noteDraft={noteDraft}
            noteSaving={noteSaving}
            onOpenAvatar={() => setAvatarModalOpen(true)}
            onOpenIdentifiers={() => setIdentifiersModalOpen(true)}
            onCopy={(value) => void copyToClipboard(value)}
            onChangeNoteDraft={setNoteDraft}
            onSetNoteEditing={setNoteEditing}
            onSaveNote={() => void saveNote()}
            sdAdminActions={enabledSdAdminActions}
            sdAdminBusyAction={sdAdminActions.busyAction}
            sdAdminResult={sdAdminActions.result}
            sdAdminEmailChoice={sdAdminActions.emailChoice}
            sdAdminSelectedEmail={sdAdminActions.selectedEmail}
            onSelectedSdAdminEmailChange={sdAdminActions.setSelectedEmail}
            onCloseSdAdminEmailChoice={sdAdminActions.closeEmailChoice}
            onRunSdAdminAction={(action, email, sendToCustomer) =>
              void sdAdminActions.runAction(action, email, sendToCustomer)
            }
            onUseSdAdminText={setReplyText}
            customerFields={sortedProjectFields}
            previousTickets={previousTickets}
            displayedPreviousTickets={displayedPreviousTickets}
            previousTicketsProjectCounts={previousTicketsProjectCounts}
            previousTicketsPreviewLimit={PREVIOUS_TICKETS_SIDEBAR_PREVIEW}
            projectPick={projectPickForPreviousList}
            previousTicketTabStateById={previousTicketTabStateById}
            onSelectPreviousTicket={onSelectPreviousTicket}
            onOpenPreviousTicketInNewTab={openPreviousTicketInNewTab}
            onShowAllPreviousTickets={() => setPreviousTicketsModalOpen(true)}
            onOpenAttachments={openAttachmentsModal}
            canViewKnowledgeBase={canViewKnowledgeBase}
            onOpenKnowledgeBase={() => {
              setSimilarPanelOpen(false)
              setKbPanelOpen((value) => !value)
            }}
            onOpenSimilarClosedTickets={() => {
              setKbPanelOpen(false)
              setSimilarPanelOpen((value) => !value)
            }}
          />
        </Flex>
      </Stack>

      {ticket?.id && ticket.project_id ? (
        <>
          {canViewKnowledgeBase ? (
          <TicketKnowledgePanel
            ticketId={ticket.id}
            operatorId={user?.id}
            projectId={ticket.project_id}
            projectName={ticket.project_name}
            opened={kbPanelOpen}
            onOpenedChange={setKbPanelOpen}
            onInsertToReply={insertAtCursor}
          />
          ) : null}
          <SimilarClosedTicketsPanel
            ticketId={ticket.id}
            operatorId={user?.id}
            projectName={ticket.project_name}
            opened={similarPanelOpen}
            onOpenedChange={setSimilarPanelOpen}
            onInsertToReply={insertAtCursor}
          />
        </>
      ) : null}

      <TicketCustomerAttachmentsModal
        opened={attachmentsModalOpen}
        loading={attachmentsLoading}
        loaded={attachmentsLoaded}
        attachments={customerAttachments}
        onClose={() => setAttachmentsModalOpen(false)}
        onOpenTicket={(selectedTicketId) => {
          setAttachmentsModalOpen(false)
          openOrSelectTab(`/tickets/${encodeURIComponent(selectedTicketId)}`)
        }}
      />

      <TicketPreviousTicketsModal
        opened={previousTicketsModalOpen}
        ticketsCount={previousTickets.length}
        items={sortedPreviousTickets}
        projectPick={projectPickForPreviousList}
        onSelectTicket={onSelectPreviousTicket}
        onOpenInNewTab={openPreviousTicketInNewTab}
        tabStateById={previousTicketTabStateById}
        onClose={() => setPreviousTicketsModalOpen(false)}
      />

      <TicketActionModals
        resolveOpened={resolveOpen}
        resolving={resolving}
        resolveConfirmationEnabled={resolveConfirmationEnabled}
        resolveComment={resolveComment}
        helpOpened={helpOpen}
        helpSending={helpSending}
        helpText={helpText}
        onCloseResolve={() => setResolveOpen(false)}
        onChangeResolveComment={setResolveComment}
        onSubmitResolve={() => void submitResolve()}
        onCloseHelp={() => setHelpOpen(false)}
        onChangeHelpText={setHelpText}
        onSubmitHelp={() => void submitHelp()}
      />

      <Modal
        opened={pendingModalOpen}
        onClose={() => {
          if (!pendingSubmitting) setPendingModalOpen(false)
        }}
        title="Перевести в ожидание"
        centered
      >
        <Stack gap="sm">
          {pendingReasons.length === 0 ? (
            <Alert color="yellow" variant="light">
              В настройках рабочего пространства не заполнен список причин ожидания.
            </Alert>
          ) : null}
          <Select
            label="Причина ожидания"
            placeholder="Выберите причину"
            data={pendingReasons.map((reason) => ({ value: reason, label: reason }))}
            value={pendingReason}
            onChange={setPendingReason}
            searchable
            required
            disabled={pendingSubmitting || pendingReasons.length === 0}
          />
          <Textarea
            label={pendingCommentRequired ? 'Комментарий' : 'Комментарий'}
            description={pendingCommentRequired ? 'Обязателен для перевода в ожидание' : 'Необязательно'}
            value={pendingComment}
            onChange={(e) => setPendingComment(e.currentTarget.value)}
            minRows={4}
            maxLength={2000}
            required={pendingCommentRequired}
            disabled={pendingSubmitting}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setPendingModalOpen(false)} disabled={pendingSubmitting}>
              Отмена
            </Button>
            <Button
              onClick={() => void submitPendingStatus()}
              loading={pendingSubmitting}
              disabled={pendingReasons.length === 0}
            >
              Перевести
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            Причина и комментарий будут отображаться в чате и в отладке истории ожидания.
          </Text>
        </Stack>
      </Modal>

      <TicketAiInstructionModal
        opened={aiInstructionModal.opened}
        loading={aiInstructionModal.loading}
        saving={aiInstructionModal.saving}
        error={aiInstructionModal.error}
        meta={aiInstructionModal.meta}
        form={aiInstructionModal.form}
        similar={aiInstructionModal.similar}
        similarReason={aiInstructionModal.similarReason}
        setForm={aiInstructionModal.setForm}
        onClose={aiInstructionModal.onClose}
        onSave={(mode) => void aiInstructionModal.onSave(mode)}
      />

      <TicketReplyToolsModals
        varsOpened={replyToolsModals.varsOpened}
        varsLoading={replyToolsModals.varsLoading}
        varsMap={replyToolsModals.varsMap}
        onCloseVars={replyToolsModals.onCloseVars}
        favoritesOpened={replyToolsModals.favoritesOpened}
        favoritesSaving={replyToolsModals.favoritesSaving}
        templates={templates}
        macros={macros}
        favoriteTemplateIds={replyToolsModals.favoriteTemplateIds}
        favoriteMacroIds={replyToolsModals.favoriteMacroIds}
        onCloseFavorites={replyToolsModals.onCloseFavorites}
        onSaveFavorites={() => void replyToolsModals.onSaveFavorites()}
        onToggleTemplate={replyToolsModals.onToggleTemplate}
        onToggleMacro={replyToolsModals.onToggleMacro}
      />

      <TicketCustomerIdentityModals
        avatarOpened={avatarModalOpen}
        avatarSrc={avatarResolvedSrc || null}
        onCloseAvatar={() => setAvatarModalOpen(false)}
        identifiersOpened={identifiersModalOpen}
        identifiers={displayIdentifiers}
        customerIdentifierRows={customerIdentifierRows}
        workspaceProjects={workspaceProjects}
        newType={identNewType}
        newValue={identNewValue}
        newDisplay={identNewDisplay}
        saving={identSaving}
        onCloseIdentifiers={() => setIdentifiersModalOpen(false)}
        onChangeNewType={setIdentNewType}
        onChangeNewValue={setIdentNewValue}
        onChangeNewDisplay={setIdentNewDisplay}
        onSubmitNewIdentifier={() => void submitNewIdentifier()}
        onDeleteIdentifier={(id) => void onDeleteIdentifier(id)}
      />

      <RefundCalculatorFloating
        opened={refundCalculatorOpen && refundCalculatorEnabled}
        operatorId={user?.id}
        onClose={() => setRefundCalculatorOpen(false)}
        onInsert={(text) => insertAtCursor(text, { withTrailingNewline: true })}
      />

      {slashUI ? (
        <Portal>
          <TicketReplySlashMenu
            slashUi={slashUI}
            menuRef={slashMenuRef}
            onPick={pickSlashItem}
          />
        </Portal>
      ) : null}
    </PageShell>
  )
}
